import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from 'context/configurationContext';
import useDisclose from 'hooks/useDisclose';
import { CLARITY_PROJECT_ID, COOKIE_CATEGORIES } from '../../config/cookiesConfig';
import { GOOGLE_ADS_ID } from '../../../../analytics/gtag';
import { setConsent } from './CookieConsent.duck';

import { Button } from '../../../../components';

import css from './CookieConsent.module.css';

// A single gtag.js load serves both the GA4 property (when one is configured) and the
// Google Ads conversion tag — the Ads tag must load even without a GA4 id, so that lead
// form conversions (CH-300) reach Google Ads.
const initGtag = (googleAnalyticsId) => {
  if (window.gtag || typeof document === 'undefined') return;

  const ga4Id = googleAnalyticsId?.startsWith('G-') ? googleAnalyticsId : null;
  if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id ?? GOOGLE_ADS_ID}`;
    document.head.appendChild(script);
  }
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  if (ga4Id) {
    window.gtag('config', ga4Id);
  }
  window.gtag('config', GOOGLE_ADS_ID);
};

const initClarity = () => {
  if (!CLARITY_PROJECT_ID || window.clarity || typeof document === 'undefined') return;

  if (!document.querySelector(`script[src*="clarity.ms/tag/${CLARITY_PROJECT_ID}"]`)) {
    const clarityScript = document.createElement('script');
    clarityScript.type = 'text/javascript';
    clarityScript.textContent = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
    `;
    document.head.appendChild(clarityScript);
  }
};

const GTM_ID = 'GTM-M4DSBV8T';

const initGTM = () => {
  if (!GTM_ID || window.google_tag_manager || typeof document === 'undefined') return;

  if (!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_ID}"]`)) {
    const gtmScript = document.createElement('script');
    gtmScript.type = 'text/javascript';
    gtmScript.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${GTM_ID}');
    `;

    document.head.appendChild(gtmScript);
  }
};

const initFBPixel = (facebookPixelId) => {
  if (typeof document === 'undefined' || !facebookPixelId) return;

  if (!document.querySelector(`script[src*="connect.facebook.net/en_US/fbevents.js"]`)) {
    const fbPixelScript = document.createElement('script');
    fbPixelScript.type = 'text/javascript';
    fbPixelScript.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${facebookPixelId}');
    `;
    fbPixelScript.nonce = window.__CSP_NONCE__ || '';
    document.head.appendChild(fbPixelScript);
  }
};

const CookieConsent = () => {
  const config = useConfiguration();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.currentUser);
  const cookieConsent = useSelector((state) => state.cookieConsent.cookieConsent);
  const isSettingConsent = useSelector((state) => state.cookieConsent.isSettingConsent);

  const googleAnalyticsId = config?.analytics?.googleAnalyticsId ?? '';
  const userCookieConsent = user?.attributes?.profile?.metadata?.cookieConsent;
  const facebookPixelId = config?.analytics?.facebookPixelId;
  const consent = user ? userCookieConsent : cookieConsent;

  const { isOpen, close } = useDisclose(!consent);

  useEffect(() => {
    if (consent === COOKIE_CATEGORIES.ALL) {
      initFBPixel(facebookPixelId);
      initGtag(googleAnalyticsId);
      initClarity();
      initGTM();
    }
  }, [consent, googleAnalyticsId, facebookPixelId]);

  const handleAcceptNecessary = useCallback(() => {
    dispatch(setConsent(COOKIE_CATEGORIES.NECESSARY));
    close();
  }, [dispatch, close]);

  const handleAcceptAll = useCallback(() => {
    dispatch(setConsent(COOKIE_CATEGORIES.ALL));
    initGtag(googleAnalyticsId);
    initClarity();
    initGTM();
    initFBPixel(facebookPixelId);
    close();
  }, [dispatch, googleAnalyticsId, close, facebookPixelId]);

  if (!isOpen) return null;

  return (
    <div className={css.root}>
      <h3 className={css.heading}>🍪 Cookies?</h3>
      <div className={css.contentWrapper}>
        <span className={css.content}>
          We use cookies to personalise your experience. Accept all cookies, or choose necessary
          only.
        </span>
      </div>
      <div className={css.buttonWrapper}>
        <Button className={css.button} onClick={handleAcceptNecessary} disabled={isSettingConsent}>
          Only Necessary
        </Button>
        <Button className={css.button} onClick={handleAcceptAll} disabled={isSettingConsent}>
          Accept All
        </Button>
      </div>
    </div>
  );
};

export default CookieConsent;
